// Vulnerability context, help, AI recommendations, integrations, priority dashboard,
// vuln data filtering, archives API
import { api } from './client';

// ============================================================================
// VULNERABILITY CONTEXT TYPES
// ============================================================================

export interface VulnerabilityContextResponse {
  vuln_id: string;
  category: string;
  simple_explanation: string;
  remediation: string;
  config_location: string;
  config_location_detail: string;
  estimated_fix_time_minutes: number;
  estimated_fix_time_note: string;
  bulk_fix_possible: boolean;
  bulk_fix_method?: string;
  risk_context: string;
  technical_details: string;
  example_fixes?: Record<string, string>;
  related_vuln_ids?: string[];
}

// Full VulnerabilityContext data with all language fields for editing
export interface VulnContextFullData {
  simple_explanation_en: string;
  simple_explanation_de: string;
  simple_explanation_fr: string;
  simple_explanation_it: string;
  remediation_en: string;
  remediation_de: string;
  remediation_fr: string;
  remediation_it: string;
  config_location: string;
  config_location_en: string;
  config_location_de: string;
  config_location_fr: string;
  config_location_it: string;
  estimated_fix_time_minutes: number;
  estimated_fix_time_note_en: string;
  estimated_fix_time_note_de: string;
  estimated_fix_time_note_fr: string;
  estimated_fix_time_note_it: string;
  bulk_fix_possible: boolean;
  bulk_fix_method_en: string;
  bulk_fix_method_de: string;
  bulk_fix_method_fr: string;
  bulk_fix_method_it: string;
  risk_context_en: string;
  risk_context_de: string;
  risk_context_fr: string;
  risk_context_it: string;
  technical_details_en: string;
  technical_details_de: string;
  technical_details_fr: string;
  technical_details_it: string;
}

// ============================================================================
// VULNERABILITY CONTEXT API
// ============================================================================

export const vulnContextAPI = {
  // Get context for a specific vulnerability by ID
  getByVulnId: (vulnId: string, lang?: string) => {
    const params = lang ? `?lang=${lang}` : '';
    return api.get<VulnerabilityContextResponse>(`/vuln-context/${encodeURIComponent(vulnId)}${params}`);
  },

  // Get context for multiple vulnerabilities at once (batch)
  getBatch: (vulnIds: string[], lang?: string) => {
    return api.post<Record<string, VulnerabilityContextResponse>>('/vuln-context/batch', {
      vuln_ids: vulnIds,
      lang: lang || 'en'
    });
  },

  // Get context for a specific scan result
  getForResult: (resultId: number, lang?: string) => {
    const params = lang ? `?lang=${lang}` : '';
    return api.get<VulnerabilityContextResponse>(`/vuln-context/for-result/${resultId}${params}`);
  },

  // Get all vulnerability contexts (admin, paginated)
  getAll: (params?: { category?: string; page?: number; limit?: number; lang?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.category) searchParams.append('category', params.category);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.lang) searchParams.append('lang', params.lang);
    const queryString = searchParams.toString();
    return api.get(`/vuln-context${queryString ? `?${queryString}` : ''}`);
  },

  // Get available categories
  getCategories: () => {
    return api.get<{ categories: string[] }>('/vuln-context/categories');
  },

  // Get estimated fix time for multiple vulnerabilities
  getEstimatedFixTime: (vulnIds: string[], lang?: string) => {
    return api.post('/vuln-context/estimate-fix-time', {
      vuln_ids: vulnIds,
      lang: lang || 'en'
    });
  },

  // Create a new vulnerability context (admin)
  create: (data: Partial<VulnerabilityContextResponse>) => {
    return api.post('/vuln-context', data);
  },

  // Update a vulnerability context (admin)
  update: (vulnId: string, data: Partial<VulnerabilityContextResponse>) => {
    return api.put(`/vuln-context/${encodeURIComponent(vulnId)}`, data);
  },

  // Delete a vulnerability context (admin)
  delete: (vulnId: string) => {
    return api.delete(`/vuln-context/${encodeURIComponent(vulnId)}`);
  },

  // Get context linked to a test definition by test_id
  getByTestId: (testId: string, lang?: string) => {
    const params = lang ? `?lang=${lang}` : '';
    return api.get<VulnerabilityContextResponse>(`/test-definitions/by-test-id/${encodeURIComponent(testId)}/context${params}`);
  },

  // Create or update context linked to a test definition by test_id
  saveForTestId: (testId: string, data: VulnContextFullData) => {
    return api.put(`/test-definitions/by-test-id/${encodeURIComponent(testId)}/context`, data);
  },
};

// ============================================================================
// HELP CONTENT TYPES
// ============================================================================

export interface HelpGuide {
  id: number;
  key: string;
  icon: string;
  icon_color: string;
  sort_order: number;
  is_active: boolean;
  translations: HelpGuideTranslation[];
  sections: HelpSection[];
}

export interface HelpGuideTranslation {
  id: number;
  guide_id: number;
  language: string;
  title: string;
  description: string;
}

export interface HelpSection {
  id: number;
  guide_id: number;
  key: string;
  sort_order: number;
  is_active: boolean;
  translations: HelpSectionTranslation[];
}

export interface HelpSectionTranslation {
  id: number;
  section_id: number;
  language: string;
  title: string;
  content: string;
}

export interface HelpFAQCategory {
  id: number;
  key: string;
  icon?: string;
  icon_color?: string;
  sort_order: number;
  is_active: boolean;
  translations: HelpFAQCategoryTranslation[];
}

export interface HelpFAQCategoryTranslation {
  id: number;
  category_id: number;
  language: string;
  name: string;
}

export interface HelpFAQ {
  id: number;
  key: string;
  category_id: number;
  sort_order: number;
  is_active: boolean;
  category?: HelpFAQCategory;
  translations: HelpFAQTranslation[];
}

export interface HelpFAQTranslation {
  id: number;
  faq_id: number;
  language: string;
  question: string;
  answer: string;
}

export interface HelpImage {
  id: number;
  filename: string;
  mime_type: string;
  size: number;
  alt_text: string;
  caption: string;
  url: string;
}

export interface HelpSetting {
  id: number;
  key: string;
  value: string;
  value_type: string;
  description: string;
}

// Formatted response matching the existing i18n structure
export interface HelpContentResponse {
  help: {
    title: string;
    subtitle: string;
    userGuides: string;
    quickAnswers: string;
    sectionsCount: string;
    questionsFound: string;
    questionFound: string;
    searchPlaceholder: string;
    noQuestionsFound: string;
    tryDifferentSearch: string;
    clearFilters: string;
    backToGuides: string;
    needMoreHelp: string;
    contactSupport: string;
    guides: Record<string, {
      title: string;
      description: string;
      [sectionKey: string]: { title: string; content: string } | string;
    }>;
    faq: {
      categories: Record<string, string>;
      items: Record<string, { question: string; answer: string }>;
    };
  };
}

// ============================================================================
// HELP CONTENT API
// ============================================================================

export const helpAPI = {
  // Get all help content in i18n-compatible format
  getContent: (lang: string = 'en') => {
    return api.get<HelpContentResponse>(`/help?lang=${lang}`);
  },

  // Get all guides
  getGuides: (lang: string = 'en') => {
    return api.get<HelpGuide[]>(`/help/guides?lang=${lang}`);
  },

  // Get a specific guide
  getGuide: (key: string, lang: string = 'en') => {
    return api.get<HelpGuide>(`/help/guides/${key}?lang=${lang}`);
  },

  // Get all FAQs
  getFAQs: (lang: string = 'en', category?: string) => {
    const params = new URLSearchParams({ lang });
    if (category) params.append('category', category);
    return api.get<HelpFAQ[]>(`/help/faqs?${params.toString()}`);
  },

  // Search FAQs
  searchFAQs: (lang: string, query: string) => {
    return api.get<HelpFAQ[]>(`/help/faqs/search?lang=${lang}&q=${encodeURIComponent(query)}`);
  },

  // Get FAQ categories
  getFAQCategories: (lang: string = 'en') => {
    return api.get<HelpFAQCategory[]>(`/help/faq-categories?lang=${lang}`);
  },

  // Get image URL
  getImageUrl: (id: number) => {
    return `/api/v1/help/images/${id}`;
  },

  // Admin: Create guide
  createGuide: (data: {
    key: string;
    icon?: string;
    icon_color?: string;
    sort_order?: number;
    translations: { language: string; title: string; description?: string }[];
  }) => {
    return api.post<HelpGuide>('/admin/help/guides', data);
  },

  // Admin: Update guide
  updateGuide: (id: number, data: {
    icon?: string;
    icon_color?: string;
    sort_order?: number;
    is_active?: boolean;
    translations?: { language: string; title: string; description?: string }[];
  }) => {
    return api.put<HelpGuide>(`/admin/help/guides/${id}`, data);
  },

  // Admin: Delete guide
  deleteGuide: (id: number) => {
    return api.delete(`/admin/help/guides/${id}`);
  },

  // Admin: Create section
  createSection: (guideId: number, data: {
    key: string;
    sort_order?: number;
    translations: { language: string; title: string; content?: string }[];
  }) => {
    return api.post<HelpSection>(`/admin/help/guides/${guideId}/sections`, data);
  },

  // Admin: Update section
  updateSection: (id: number, data: {
    sort_order?: number;
    is_active?: boolean;
    translations?: { language: string; title: string; content?: string }[];
  }) => {
    return api.put<HelpSection>(`/admin/help/sections/${id}`, data);
  },

  // Admin: Delete section
  deleteSection: (id: number) => {
    return api.delete(`/admin/help/sections/${id}`);
  },

  // Admin: Create FAQ
  createFAQ: (data: {
    key: string;
    category_id: number;
    sort_order?: number;
    translations: { language: string; question: string; answer?: string }[];
  }) => {
    return api.post<HelpFAQ>('/admin/help/faqs', data);
  },

  // Admin: Update FAQ
  updateFAQ: (id: number, data: {
    category_id?: number;
    sort_order?: number;
    is_active?: boolean;
    translations?: { language: string; question: string; answer?: string }[];
  }) => {
    return api.put<HelpFAQ>(`/admin/help/faqs/${id}`, data);
  },

  // Admin: Delete FAQ
  deleteFAQ: (id: number) => {
    return api.delete(`/admin/help/faqs/${id}`);
  },

  // Admin: Create FAQ category
  createFAQCategory: (data: {
    key: string;
    icon?: string;
    icon_color?: string;
    sort_order?: number;
    translations: { language: string; name: string }[];
  }) => {
    return api.post<HelpFAQCategory>('/admin/help/faq-categories', data);
  },

  // Admin: Update FAQ category
  updateFAQCategory: (id: number, data: {
    icon?: string;
    icon_color?: string;
    sort_order?: number;
    is_active?: boolean;
    translations?: { language: string; name: string }[];
  }) => {
    return api.put<HelpFAQCategory>(`/admin/help/faq-categories/${id}`, data);
  },

  // Admin: Delete FAQ category
  deleteFAQCategory: (id: number) => {
    return api.delete(`/admin/help/faq-categories/${id}`);
  },

  // Admin: Upload image
  uploadImage: (file: File, altText?: string, caption?: string) => {
    const formData = new FormData();
    formData.append('image', file);
    if (altText) formData.append('alt_text', altText);
    if (caption) formData.append('caption', caption);
    return api.post<HelpImage>('/admin/help/images', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Admin: List images
  listImages: () => {
    return api.get<HelpImage[]>('/admin/help/images');
  },

  // Admin: Delete image
  deleteImage: (id: number) => {
    return api.delete(`/admin/help/images/${id}`);
  },

  // Admin: Get settings
  getSettings: () => {
    return api.get<HelpSetting[]>('/admin/help/settings');
  },

  // Admin: Update setting
  updateSetting: (key: string, value: string) => {
    return api.put<HelpSetting>(`/admin/help/settings/${key}`, { value });
  },
};

// ============================================================================
// AI RECOMMENDATIONS TYPES
// ============================================================================

export interface AIRecommendation {
  id: number;
  type: string;
  title: string;
  description: string;
  priority: number;
  confidence: number;
  affected_findings: number[];
  suggested_actions: string[];
  created_at: string;
}

export interface AIPreferences {
  enabled: boolean;
  auto_generate: boolean;
  model: string;
  max_recommendations: number;
  min_confidence: number;
}

// ============================================================================
// AI RECOMMENDATIONS API
// ============================================================================

export const aiRecommendationsAPI = {
  // Get AI recommendation system status
  getStatus: () => {
    console.log('🤖 Fetching AI recommendation status');
    return api.get('/ai-recommendations/status');
  },

  // Get recommendation history
  getHistory: (params?: { limit?: number; offset?: number }) => {
    console.log('📜 Fetching AI recommendation history');
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());
    const query = searchParams.toString();
    return api.get(`/ai-recommendations/history${query ? `?${query}` : ''}`);
  },

  // Get recommendations for a scan
  getScanRecommendations: (scanId: number) => {
    console.log('🔍 Fetching AI recommendations for scan:', scanId);
    return api.get<AIRecommendation[]>(`/scans/${scanId}/ai-recommendations`);
  },

  // Generate recommendations for a scan
  generateScanRecommendations: (scanId: number) => {
    console.log('⚡ Generating AI recommendations for scan:', scanId);
    return api.post(`/scans/${scanId}/ai-recommendations`);
  },

  // Get recommendations for a project
  getProjectRecommendations: (projectId: number) => {
    console.log('🔍 Fetching AI recommendations for project:', projectId);
    return api.get<AIRecommendation[]>(`/projects/${projectId}/ai-recommendations`);
  },

  // Generate recommendations for a project
  generateProjectRecommendations: (projectId: number) => {
    console.log('⚡ Generating AI recommendations for project:', projectId);
    return api.post(`/projects/${projectId}/ai-recommendations`);
  },

  // Get client AI preferences
  getClientPreferences: (clientId: number) => {
    console.log('⚙️ Fetching AI preferences for client:', clientId);
    return api.get<AIPreferences>(`/clients/${clientId}/ai-preferences`);
  },

  // Update client AI preferences
  updateClientPreferences: (clientId: number, preferences: Partial<AIPreferences>) => {
    console.log('💾 Updating AI preferences for client:', clientId);
    return api.put(`/clients/${clientId}/ai-preferences`, preferences);
  },
};

// ============================================================================
// INTEGRATION WEBHOOKS TYPES
// ============================================================================

export interface IntegrationWebhook {
  id: number;
  name: string;
  url: string;
  type: 'slack' | 'teams' | 'discord' | 'generic' | 'jira' | 'pagerduty';
  events: string[];
  enabled: boolean;
  secret?: string;
  headers?: Record<string, string>;
  created_at: string;
  updated_at: string;
  last_triggered?: string;
  last_status?: 'success' | 'failed';
}

export interface WebhookLog {
  id: number;
  webhook_id: number;
  event_type: string;
  status: 'success' | 'failed';
  request_body: string;
  response_code: number;
  response_body?: string;
  error?: string;
  created_at: string;
}

// ============================================================================
// INTEGRATIONS API
// ============================================================================

export const integrationsAPI = {
  // Get all webhooks
  getWebhooks: () => {
    console.log('🔗 Fetching integration webhooks');
    return api.get<{ webhooks: IntegrationWebhook[] }>('/integrations');
  },

  // Create a new webhook
  createWebhook: (data: Omit<IntegrationWebhook, 'id' | 'created_at' | 'updated_at' | 'last_triggered' | 'last_status'>) => {
    console.log('➕ Creating integration webhook:', data.name);
    return api.post<IntegrationWebhook>('/integrations', data);
  },

  // Get a specific webhook
  getWebhook: (id: number) => {
    console.log('🔍 Fetching integration webhook:', id);
    return api.get<IntegrationWebhook>(`/integrations/${id}`);
  },

  // Update a webhook
  updateWebhook: (id: number, data: Partial<IntegrationWebhook>) => {
    console.log('✏️ Updating integration webhook:', id);
    return api.put<IntegrationWebhook>(`/integrations/${id}`, data);
  },

  // Delete a webhook
  deleteWebhook: (id: number) => {
    console.log('🗑️ Deleting integration webhook:', id);
    return api.delete(`/integrations/${id}`);
  },

  // Test a webhook
  testWebhook: (id: number) => {
    console.log('🧪 Testing integration webhook:', id);
    return api.post<{ success: boolean; response_code?: number; error?: string }>(`/integrations/${id}/test`);
  },

  // Get webhook logs
  getWebhookLogs: (id: number, params?: { limit?: number; offset?: number }) => {
    console.log('📜 Fetching webhook logs:', id);
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());
    const query = searchParams.toString();
    return api.get<{ logs: WebhookLog[]; total: number; page: number; limit: number }>(`/integrations/${id}/logs${query ? `?${query}` : ''}`);
  },

  // Jira-specific endpoints
  bulkCreateJiraTickets: (data: { webhook_id: number; finding_ids: number[] }) => {
    return api.post('/integrations/jira/bulk-create', data);
  },

  syncJiraStatuses: (webhookId: number) => {
    return api.post('/integrations/jira/sync', { webhook_id: webhookId });
  },

  getJiraProjects: (webhookId: number) => {
    return api.get<{ projects: { key: string; name: string }[] }>(`/integrations/jira/${webhookId}/projects`);
  },

  getJiraIssueTypes: (webhookId: number, projectKey: string) => {
    return api.get<{ issue_types: { id: string; name: string }[] }>(`/integrations/jira/${webhookId}/issue-types?project=${projectKey}`);
  },

  // Scan diff
  getScanDiff: (scanId: number) => {
    return api.get(`/scans/${scanId}/scan-diff`);
  },
};

// ============================================================================
// PRIORITY DASHBOARD TYPES
// ============================================================================

export interface PriorityDashboard {
  total_findings: number;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  top_priorities: PriorityItem[];
  risk_trend: { date: string; score: number }[];
}

export interface PriorityItem {
  id: number;
  title: string;
  severity: string;
  priority_score: number;
  host: string;
  cve_id?: string;
  fix_complexity: 'easy' | 'medium' | 'hard';
  business_impact: 'low' | 'medium' | 'high' | 'critical';
}

// ============================================================================
// PRIORITY DASHBOARD API
// ============================================================================

export const priorityDashboardAPI = {
  // Get priority dashboard overview
  getDashboard: () => {
    console.log('📊 Fetching priority dashboard');
    return api.get<PriorityDashboard>('/priorities/dashboard');
  },

  // Get top priorities across all scans
  getTopPriorities: (params?: { limit?: number; severity?: string }) => {
    console.log('🔝 Fetching top priorities');
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.severity) searchParams.append('severity', params.severity);
    const query = searchParams.toString();
    return api.get<PriorityItem[]>(`/priorities/top${query ? `?${query}` : ''}`);
  },

  // Recalculate priority scores for a scan
  recalculatePriorities: (scanId: number) => {
    console.log('🔄 Recalculating priority scores for scan:', scanId);
    return api.post(`/scans/${scanId}/priorities/recalculate`);
  },

  // Get priority score for a single result
  getResultPriority: (resultId: number) => {
    console.log('📈 Fetching priority score for result:', resultId);
    return api.get<PriorityItem>(`/results/${resultId}/priority`);
  },
};

// ============================================================================
// VULNDATA FILTERING TYPES
// ============================================================================

export interface FilterCriteria {
  source: string;
  available_fields: string[];
  sample_values: Record<string, string[]>;
}

export interface FilteringStats {
  total_rules: number;
  active_rules: number;
  filtered_today: number;
  by_source: Record<string, number>;
}

// ============================================================================
// VULNDATA FILTERING API
// ============================================================================

export const vulnDataFilteringAPI = {
  // Bulk filter existing data
  bulkFilter: (filters: { sources?: string[]; severities?: string[]; date_range?: { start: string; end: string } }) => {
    console.log('🔍 Bulk filtering vulnerability data');
    return api.post('/vulndata/filter/bulk', filters);
  },

  // Bulk filter by custom criteria
  bulkFilterCustom: (criteria: Record<string, any>) => {
    console.log('🔍 Bulk filtering by custom criteria');
    return api.post('/vulndata/filter/bulk-custom', criteria);
  },

  // Bulk activate by source
  bulkActivateBySource: (sources: string[]) => {
    console.log('✅ Bulk activating by source:', sources);
    return api.post('/vulndata/filter/bulk-activate', { sources });
  },

  // Bulk deactivate unknown fields
  bulkDeactivateUnknown: () => {
    console.log('🚫 Bulk deactivating unknown fields');
    return api.post('/vulndata/filter/bulk-deactivate-unknown');
  },

  // Get filter criteria for a source
  getFilterCriteria: (source: string) => {
    console.log('📋 Fetching filter criteria for source:', source);
    return api.get<FilterCriteria>(`/vulndata/filter/criteria/${source}`);
  },

  // Get filtering statistics
  getFilteringStats: () => {
    console.log('📊 Fetching filtering statistics');
    return api.get<FilteringStats>('/vulndata/filter/stats');
  },
};

// ============================================================================
// SCAN ARCHIVES API
// ============================================================================

export const archiveAPI = {
  list: (params?: { page?: number; page_size?: number; project_id?: number; has_source_data?: string; date_from?: string; date_to?: string; search?: string }) => {
    console.log('📦 Fetching scan archives');
    return api.get('/admin/archives', { params });
  },
  stats: () => {
    console.log('📊 Fetching archive stats');
    return api.get('/admin/archives/stats');
  },
  get: (id: number) => {
    console.log('📄 Fetching archive detail:', id);
    return api.get(`/admin/archives/${id}`);
  },
  getReport: (id: number) => {
    console.log('📋 Fetching archive report:', id);
    return api.get(`/admin/archives/${id}/report`, { responseType: 'blob' });
  },
  purge: (id: number) => {
    console.log('🗑️ Purging archive source data:', id);
    return api.post(`/admin/archives/${id}/purge`);
  },
  bulkPurge: (olderThanDays: number, projectId?: number) => {
    console.log('🗑️ Bulk purging archives older than', olderThanDays, 'days');
    return api.post('/admin/archives/bulk-purge', {
      older_than_days: olderThanDays,
      project_id: projectId,
    });
  },
  backfill: () => {
    console.log('🔄 Starting archive backfill');
    return api.post('/admin/archives/backfill');
  },
};
